import { getDb } from '@/services/db/database';
import { generateId } from '@/utils/id';
import { nowIso } from '@/utils/date';
import type { PhotoBlob } from '@/types/cv';

const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.85;

export async function resizeAndCompressImage(
  file: Blob,
): Promise<{ blob: Blob; mimeType: string }> {
  const bitmap = await createImageBitmap(file);
  const ratio = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const targetW = Math.round(bitmap.width * ratio);
  const targetH = Math.round(bitmap.height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Brak wsparcia canvas 2d');
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close?.();

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Konwersja obrazu nie powiodła się'))),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

  return { blob, mimeType: 'image/jpeg' };
}

export const photoRepository = {
  async save(file: Blob): Promise<PhotoBlob> {
    const { blob, mimeType } = await resizeAndCompressImage(file);
    const photo: PhotoBlob = {
      id: generateId(),
      blob,
      mimeType,
      createdAt: nowIso(),
    };
    await getDb().photos.put(photo);
    return photo;
  },

  async saveRaw(blob: Blob, mimeType = 'image/jpeg'): Promise<PhotoBlob> {
    const photo: PhotoBlob = {
      id: generateId(),
      blob,
      mimeType,
      createdAt: nowIso(),
    };
    await getDb().photos.put(photo);
    return photo;
  },

  async getById(id: string): Promise<PhotoBlob | undefined> {
    return getDb().photos.get(id);
  },

  async delete(id: string): Promise<void> {
    await getDb().photos.delete(id);
  },

  async getDataUrl(id: string): Promise<string | null> {
    const photo = await this.getById(id);
    if (!photo) return null;
    return blobToDataUrl(photo.blob);
  },
};

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}
